import React, { useEffect, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { Unit } from "@/lib/api";
import { statusBadgeProps, statusBadgeLabel, formatNum } from "@/lib/unit-helpers";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/app/AuthContext";
import { useT } from "@/app/localization";
import { energyFromWh } from "@/app/displayFormat";

function clamp(n: number, a: number, b: number) {
  return Math.max(a, Math.min(b, n));
}

interface ChargerVitrineProps {
  units: Unit[];
  total: number;
  isLoading: boolean;
  onUnitClick: (unit: Unit) => void;
  /** Double-click a tile to open the full charger details page. */
  onUnitDoubleClick?: (unit: Unit) => void;
  selectedUnitId?: string;
}

export function ChargerVitrine({ units, total, isLoading, onUnitClick, onUnitDoubleClick, selectedUnitId }: ChargerVitrineProps) {
  const t = useT();
  const parentRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(1100);

  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setContainerW(el.clientWidth || 1100));
    ro.observe(el);
    setContainerW(el.clientWidth || 1100);
    return () => ro.disconnect();
  }, []);

  const itemMinW = 320;
  const gap = 16;
  const cols = clamp(Math.floor((containerW + gap) / (itemMinW + gap)), 1, 5);
  const rowCount = Math.ceil(units.length / cols) || 0;

  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 236,
    overscan: 6,
  });

  return (
    <div className="rounded-xl bg-card border border-border">
      <div className="p-0">
        <div ref={parentRef} className="h-[calc(100vh-330px)] min-h-[520px] overflow-auto rounded-2xl">
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((row) => {
              const start = row.index * cols;
              const end = Math.min(units.length, start + cols);
              const items = units.slice(start, end);

              return (
                <div
                  key={row.key}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${row.start}px)`,
                    padding: 16,
                  }}
                >
                  <div
                    className="grid gap-4"
                    style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
                  >
                    {items.map((u) => (
                      <VitrinaCard
                        key={u.unit_id}
                        unit={u}
                        selected={u.unit_id === selectedUnitId}
                        onClick={() => onUnitClick(u)}
                        onDoubleClick={onUnitDoubleClick ? () => onUnitDoubleClick(u) : undefined}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {!isLoading && units.length === 0 && (
            <div className="p-10 text-center opacity-70">{t("operator.units.none_found")}</div>
          )}
          {isLoading && units.length === 0 && (
            <div className="p-10 text-center opacity-60">{t("common.state.loading")}</div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-border">
          <div className="text-sm opacity-70">
            {t("operator.units.showing", { shown: units.length, total })}
          </div>
          <div className="text-xs opacity-60">{t("operator.units.grid_columns", { count: cols })}</div>
        </div>
      </div>
    </div>
  );
}

function VitrinaCard({ unit, selected, onClick, onDoubleClick }: { unit: Unit; selected: boolean; onClick: () => void; onDoubleClick?: () => void }) {
  const { isAdmin } = useAuth();
  const t = useT();
  const online = unit.is_online !== false;
  const rawState = unit.state_name || "";
  const bp = statusBadgeProps(rawState, online);
  const statusLabel = statusBadgeLabel(t, rawState, online);
  const product = (unit as any).product_class_short || t("operator.units.value_unknown");
  const energy = energyFromWh(unit.session_energy ?? 0);
  // Admin-only diagnostic: charger protocol version (v0 = legacy no-checksum,
  // flagged amber to match the AdminPanel Protocol Versions widget).
  const proto = (unit as any).protocol_version;
  const showProto = isAdmin && proto !== null && proto !== undefined && proto !== "";

  return (
    <div
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      className={[
        onDoubleClick ? "select-none" : "",
        "rounded-xl bg-card border border-border shadow-sm transition cursor-pointer",
        "hover:shadow-md hover:-translate-y-[1px] hover:border-primary/30",
        selected ? "ring-2 ring-primary border-primary/40" : "",
        unit.is_online === false ? "opacity-60" : "",
      ].join(" ")}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold truncate">{unit.name}</div>
            <div className="font-mono text-xs opacity-60 truncate">{unit.unit_id}</div>
            <div className="text-xs opacity-60 mt-1 flex items-center gap-1.5">
              <span>{product}</span>
              {showProto && (
                <Badge
                  variant={String(proto) === "0" ? "warning" : "neutral"}
                  outline
                  className="text-[10px] px-1.5"
                >
                  {t("operator.units.protocol_badge", { version: String(proto) })}
                </Badge>
              )}
            </div>
            {(unit as any).location && (
              <div className="text-xs opacity-50 truncate mt-0.5">{(unit as any).location}</div>
            )}
          </div>
          <Badge variant={bp.variant} outline={bp.outline}>
            {statusLabel}
          </Badge>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <div className="opacity-70">{t("operator.metric.voltage")}</div>
          <div className="text-right font-semibold">{formatNum(unit.voltage, 1)}V</div>
          <div className="opacity-70">{t("operator.metric.current")}</div>
          <div className="text-right font-semibold">{formatNum(unit.current, 1)}A</div>
          <div className="opacity-70">{t("operator.metric.session_energy_short")}</div>
          <div className="text-right font-semibold">{energy}</div>
          <div className="opacity-70">{t("operator.metric.power_factor_short")}</div>
          <div className="text-right font-semibold">{formatNum(unit.power_factor, 2)}</div>
        </div>
      </div>
    </div>
  );
}
